import { useCityBoundary } from '../../features/queries/getCityBoundary';
import { searchReports } from '../../features/queries/searchReports';
import { shortId } from '../../lib/ids';
import { plusCodeToCoordinates } from '../../lib/plusCode';
import { useQ } from '../../lib/query';
import { __ } from '../../utils';
import { usePlaceSuggestions } from './usePlaceSuggestions';
import CloseIcon from '@mui/icons-material/Close';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Stack,
} from '@presov.sk/presov-ui-react';
import React from 'react';

/** Kým je text kratší, hľadať nemá zmysel - vrátilo by sa pol mesta. */
const MIN_TERM = 2;
/** Písanie sa nechá dopísať; až potom idú dotazy von. */
const DEBOUNCE = 250;

export type MapSearchItem =
  | { kind: 'place'; key: string; label: string; hint?: string }
  | { kind: 'plusCode'; key: string; label: string; hint?: string }
  | { kind: 'report'; key: string; label: string; hint?: string };

type Props = {
  /** Pohľad mapy - podľa neho sa uprednostnia miesta v okolí výrezu. */
  view?: __esri.MapView | null;
  /** `lon,lat` výrezu; podnety bližšie k nemu idú prvé. */
  location?: string;
  /**
   * Vybrané miesto alebo plus kód: `[lng, lat]` a popis, ktorý sa má na mape
   * ukázať pri značke. Popis nesie hľadanie, nie mapa - mapa o tom, čo
   * používateľ napísal, nevie nič.
   */
  onSelectPlace: (coordinates: [number, number], label: string) => void;
  /** Vybraný podnet - appková podoba identifikátora. */
  onSelectReport: (reportId: string, coordinates?: [number, number]) => void;
};

/**
 * Hľadanie nad mapou na hlavnej stránke.
 *
 * Nahradilo `arcgis-search`. Dôvod nie je vzhľad: widget vie ponúknuť len
 * miesta, kým tu treba v jednom zozname aj **podnety** (podľa názvu, popisu
 * alebo ID, presne ako v administrácii) a **plus kód**. Rozšíriť widget o zdroj
 * sa dá len `SearchSource`-om, ktorý kreslí výsledky vo svojom shadow DOM -
 * teda bez témy a bez komponentov z `ui-react`, rovnaký problém ako pri
 * popupoch (viď `.docs/vyvoj/mapa.md`). Miesta preto ťaháme z jeho **view
 * modelu** a kreslíme si zoznam sami.
 *
 * Plus kód sa nehľadá, len rozpoznáva - je to súradnica zapísaná inak, takže na
 * neho netreba dotaz a ponuka je hotová hneď pri dopísaní.
 */
const MapSearch: React.FC<Props> = ({
  view,
  location,
  onSelectPlace,
  onSelectReport,
}) => {
  const [term, setTerm] = React.useState('');
  const [debounced, setDebounced] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const id = window.setTimeout(() => setDebounced(term.trim()), DEBOUNCE);
    return () => window.clearTimeout(id);
  }, [term]);

  const query = debounced.length >= MIN_TERM ? debounced : '';

  /**
   * Referencia pre skrátený plus kód. Berie sa zo stredu výrezu, ktorý panel
   * dostáva na radenie podnetov - skrátený kód platí len v okolí známeho
   * miesta, takže bez nej by sa nedekódoval vôbec.
   */
  const reference = React.useMemo<[number, number] | undefined>(() => {
    const [lng, lat] = (location ?? '').split(',').map(Number);
    return Number.isFinite(lng) && Number.isFinite(lat)
      ? [lng, lat]
      : undefined;
  }, [location]);

  const { isInside } = useCityBoundary();

  // Plus kód mimo mesta sa v ponuke ani neukáže. Je to platná súradnica, takže
  // by ho ponuka inak ochotne prijala a odletela s mapou mimo Prešova - pritom
  // podnet sa tam nahlásiť nedá.
  const plusCode = React.useMemo(() => {
    const decoded = plusCodeToCoordinates(term, reference);
    return decoded && isInside(decoded[0], decoded[1]) ? decoded : undefined;
  }, [term, reference, isInside]);

  const { results: places, resolve } = usePlaceSuggestions(
    // Plus kód je súradnica, nie adresa - geokóder by na neho aj tak nič
    // nenašiel a dotaz by bol zbytočný.
    plusCode ? '' : query,
    view,
  );

  const { data: reports = [], isFetching } = useQ(
    searchReports,
    { q: query, location },
    { enabled: !!query && !plusCode },
  );

  const items = React.useMemo<MapSearchItem[]>(() => {
    if (plusCode) {
      return [
        {
          kind: 'plusCode',
          key: 'plus-code',
          label: term.trim().toUpperCase(),
          hint: __('Prejsť na miesto'),
        },
      ];
    }

    return [
      ...reports.map((report) => ({
        kind: 'report' as const,
        key: report.id,
        label: report.title,
        hint: shortId(report.id),
      })),
      ...places.map((place, index) => ({
        kind: 'place' as const,
        key: `place-${place.key ?? index}`,
        label: place.text ?? '',
        hint: undefined,
      })),
    ];
  }, [plusCode, term, reports, places]);

  // Klik mimo panel zoznam zavrie. Text v poli zostáva - hľadanie sa nezruší,
  // len sa prestane pýtať o pozornosť.
  React.useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  React.useEffect(() => {
    setActiveIndex(-1);
  }, [items.length]);

  const select = React.useCallback(
    async (item: MapSearchItem) => {
      setOpen(false);

      if (item.kind === 'plusCode') {
        if (plusCode) onSelectPlace(plusCode, item.label);
        return;
      }

      if (item.kind === 'report') {
        const report = reports.find(({ id }) => id === item.key);
        const coordinates = report?.location?.coordinates as
          | [number, number]
          | undefined;
        onSelectReport(item.key, coordinates);
        return;
      }

      const suggestion = places.find(
        (place, index) => `place-${place.key ?? index}` === item.key,
      );
      if (!suggestion) return;

      const coordinates = await resolve(suggestion);

      // Miesta sa nedajú odfiltrovať už v ponuke: `SuggestResult` z ArcGISu
      // nesie len text a `magicKey`, súradnice prídu až z `resolve`, teda za
      // ďalšie sieťové volanie na **každý** návrh. Kontrola je preto tu, pri
      // výbere - používateľ tak mimo mesta neodletí, aj keď mu geokóder
      // takú adresu ponúkne.
      if (!coordinates || !isInside(coordinates[0], coordinates[1])) {
        return;
      }

      setTerm(item.label);
      onSelectPlace(coordinates, item.label);
    },
    [
      plusCode,
      reports,
      places,
      resolve,
      onSelectPlace,
      onSelectReport,
      isInside,
    ],
  );

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }

    // Rozpoznaný plus kód je súradnica, nie dotaz - `Enter` na neho skočí bez
    // ohľadu na to, či je zoznam otvorený a čo je v ňom vybrané. Kým to viselo
    // na `open` a `activeIndex`, stačilo zavrieť zoznam `Escape`om a ten istý
    // text padol do hľadania miesta, ktoré na kód vrátilo hocijakú obec.
    if (event.key === 'Enter' && plusCode) {
      event.preventDefault();
      setOpen(false);
      onSelectPlace(plusCode, term.trim().toUpperCase());
      return;
    }

    if (!open || !items.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((index) =>
        Math.min(Math.max(index + step, 0), items.length - 1),
      );
      return;
    }

    if (event.key === 'Enter') {
      const item = items[activeIndex] ?? items[0];
      if (item) {
        event.preventDefault();
        void select(item);
      }
    }
  };

  const showPanel = open && !!query;
  const groupLabel = (index: number): string | null => {
    const item = items[index];
    const previous = items[index - 1];
    if (!item || (previous && previous.kind === item.kind)) return null;
    if (item.kind === 'report') return __('Podnety');
    if (item.kind === 'plusCode') return __('Plus kód');
    return __('Miesta');
  };

  return (
    <Box ref={rootRef} sx={{ width: { xs: 1, sm: 320 }, maxWidth: 1 }}>
      <Stack
        direction='row'
        sx={(theme) => ({
          alignItems: 'center',
          gap: 1,
          // Rovnaká výška ako legenda a tlačidlo nového podnetu - prekryvy
          // mapy majú byť jeden rad rovnako vysokých piluliek.
          height: theme.typography.pxToRem(38),
          paddingLeft: 1.5,
          paddingRight: 0.5,
          borderRadius: `${theme.shape.borderRadiusFull}px`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.overlayShadow.menu,
        })}
      >
        <SearchIcon sx={{ fontSize: 18, color: 'grey.500', flex: 'none' }} />
        <Box
          component='input'
          value={term}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setTerm(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={__('Nájsť adresu, miesto alebo podnet')}
          aria-label={__('Hľadať na mape')}
          sx={(theme) => ({
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontFamily: 'inherit',
            fontSize: theme.typography.pxToRem(14),
            color: theme.palette.text.primary,
            '&::placeholder': { color: theme.palette.grey[400] },
          })}
        />
        {isFetching ? (
          <CircularProgress size={16} sx={{ flex: 'none', mr: 0.75 }} />
        ) : term ? (
          <IconButton
            clear
            aria-label={__('Vymazať hľadanie')}
            onClick={() => {
              setTerm('');
              setOpen(false);
            }}
            sx={{ flex: 'none', color: 'grey.500' }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
      </Stack>

      {showPanel ? (
        <Box
          role='listbox'
          sx={(theme) => ({
            marginTop: 0.75,
            padding: 0.75,
            maxHeight: theme.typography.pxToRem(320),
            overflowY: 'auto',
            borderRadius: `${theme.typography.pxToRem(12)}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.overlayShadow.menu,
          })}
        >
          {items.length ? (
            items.map((item, index) => {
              const label = groupLabel(index);

              return (
                <React.Fragment key={item.key}>
                  {label ? (
                    <Box
                      sx={(theme) => ({
                        padding: theme.spacing(1, 1.25, 0.5),
                        fontSize: theme.typography.pxToRem(11),
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: theme.palette.grey[500],
                      })}
                    >
                      {label}
                    </Box>
                  ) : null}

                  <ButtonBase
                    role='option'
                    aria-selected={index === activeIndex}
                    onClick={() => void select(item)}
                    sx={(theme) => ({
                      width: 1,
                      gap: 1.25,
                      justifyContent: 'flex-start',
                      padding: theme.spacing(0.875, 1.25),
                      borderRadius: `${theme.shape.borderRadiusSm}px`,
                      textAlign: 'left',
                      backgroundColor:
                        index === activeIndex
                          ? theme.palette.grey[100]
                          : 'transparent',
                      '&:hover': { backgroundColor: theme.palette.grey[50] },
                    })}
                  >
                    <Box
                      component='span'
                      sx={{ flex: 'none', display: 'flex', color: 'grey.500' }}
                    >
                      {item.kind === 'report' ? (
                        <ReportProblemOutlinedIcon sx={{ fontSize: 17 }} />
                      ) : (
                        <PlaceOutlinedIcon sx={{ fontSize: 17 }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={(theme) => ({
                          fontSize: theme.typography.pxToRem(13),
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        })}
                      >
                        {item.label}
                      </Box>
                      {item.hint ? (
                        <Box
                          sx={(theme) => ({
                            fontSize: theme.typography.pxToRem(11),
                            color: theme.palette.grey[500],
                            fontFamily:
                              item.kind === 'report'
                                ? theme.typography.fontFamilyMono
                                : undefined,
                          })}
                        >
                          {item.hint}
                        </Box>
                      ) : null}
                    </Box>
                  </ButtonBase>
                </React.Fragment>
              );
            })
          ) : (
            <Box
              sx={(theme) => ({
                padding: theme.spacing(1.5, 1.25),
                fontSize: theme.typography.pxToRem(13),
                color: theme.palette.grey[500],
              })}
            >
              {isFetching ? __('Hľadá sa…') : __('Žiadne výsledky')}
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default MapSearch;
